import React from "react";
import { CheckCircle2, User, Shield, Settings, FileText } from "lucide-react";
import { FormData } from "../../../hooks/useCreateRecordForm";
import { Badge } from "../../ui/badge";
import { renewalReviewText } from "../../../utils/folders/renewalReviewText.ts";

interface Step5ReviewProps {
  formData: FormData;
  selectedTemplates: string[];
  isAnimating: boolean;
}

const templateNames: Record<string, string> = {
  "tenant-pf-cja": "Inquilino PF - CJA",
  "tenant-pm-cja": "Inquilino PM - CJA",
  "guarantor-pf-cja": "Fiador PF - CJA",
  "guarantor-pm-cja": "Fiador PM - CJA",
  "collateral": "Inmueble en garantía",
  "lease-contract-pm": "Contrato de Arrendamiento PM",
  "lease-contract-pf": "Contrato de Arrendamiento PF",
  "owner-representative": "Representante del propietario",
};

export const Step5Review: React.FC<Step5ReviewProps> = ({
  formData,
  selectedTemplates,
  isAnimating,
}) => {
  const renewalText = renewalReviewText(
    formData.isRenewal,
    formData.previousFolio,
  );

  return (
    <div
      className={`space-y-6 transition-all duration-500 ${isAnimating ? "opacity-0 translate-x-4" : "opacity-100 translate-x-0"}`}
    >
      <div className="space-y-2">
        <div className="flex items-center gap-3 pb-2">
          <CheckCircle2 className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            Resumen de tu Expediente
          </h3>
        </div>
        <p className="text-sm text-gray-500">
          Revisa los detalles antes de crear el expediente
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Datos del Inquilino */}
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-4 h-4 text-gray-600" />
            <h4 className="text-sm font-semibold text-gray-900">Datos del Inquilino</h4>
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium text-gray-900">
                {formData.tenantName} {formData.tenantFirstSurname} {formData.tenantSecondSurname}
              </p>
              <Badge variant="outline" className="text-xs mt-1">
                Persona {formData.tenantType === "fisica" ? "Física" : "Moral"}
              </Badge>
              {formData.isRenewal && (
                <Badge
                  variant="outline"
                  className="text-xs mt-1 ml-1 border-blue-300 text-blue-700"
                >
                  Renovación de contrato
                </Badge>
              )}
            </div>
            <div className="space-y-2 pt-2 border-t border-gray-100">
              <div className="flex items-start gap-2">
                <span className="text-xs text-gray-600">Correo:</span>
                <span className="text-xs text-gray-900 break-all">{formData.tenantEmail}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-xs text-gray-600">Teléfono:</span>
                <span className="text-xs text-gray-900">{formData.tenantPhoneNumber}</span>
              </div>
              {formData.tenantRfc && (
                <div className="flex items-start gap-2">
                  <span className="text-xs text-gray-600">RFC:</span>
                  <span className="text-xs text-gray-900">{formData.tenantRfc}</span>
                </div>
              )}
              {formData.tenantCurp && (
                <div className="flex items-start gap-2">
                  <span className="text-xs text-gray-600">CURP:</span>
                  <span className="text-xs text-gray-900">{formData.tenantCurp}</span>
                </div>
              )}
              {renewalText && (
                <div className="flex items-start gap-2">
                  <span className="text-xs text-gray-600">
                    Renovación de contrato:
                  </span>
                  <span className="text-xs text-gray-900">{renewalText}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Datos del Asesor */}
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="flex items-center gap-2 mb-4">
            <Settings className="w-4 h-4 text-gray-600" />
            <h4 className="text-sm font-semibold text-gray-900">Datos del Asesor</h4>
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium text-gray-900">
                {formData.advisorName} {formData.advisorFirstSurname} {formData.advisorSecondSurname}
              </p>
            </div>
            <div className="space-y-2 pt-2 border-t border-gray-100">
              <div className="flex items-start gap-2">
                <span className="text-xs text-gray-600">Correo:</span>
                <span className="text-xs text-gray-900 break-all">{formData.advisorEmail}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-xs text-gray-600">Teléfono:</span>
                <span className="text-xs text-gray-900">{formData.advisorPhoneNumber}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Guarantor and Configuration Summary */}
      <div className="space-y-4">
        {/* Garantías */}
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-4 h-4 text-gray-600" />
            <h4 className="text-sm font-semibold text-gray-900">Configuración de Garantías</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600">Requiere fiador:</span>
              <Badge variant={formData.requiresGuarantor ? "default" : "outline"} className="text-xs">
                {formData.requiresGuarantor ? "Sí" : "No"}
              </Badge>
            </div>
            {formData.requiresGuarantor && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">Tipo:</span>
                  <Badge variant="outline" className="text-xs">
                    {formData.guarantorType === "fisica" ? "Persona Física" : "Persona Moral"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">Inmueble como garantía:</span>
                  <Badge variant={formData.propertyAsGuarantee ? "default" : "outline"} className="text-xs">
                    {formData.propertyAsGuarantee ? "Sí" : "No"}
                  </Badge>
                </div>
                {formData.guarantorEmail && (
                  <div className="md:col-span-2 pt-2 border-t border-gray-100">
                    <div className="flex items-start gap-2">
                      <span className="text-xs text-gray-600">Correo del Fiador:</span>
                      <span className="text-xs text-gray-900 break-all">{formData.guarantorEmail}</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Convenio y Representante */}
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-4 h-4 text-gray-600" />
            <h4 className="text-sm font-semibold text-gray-900">Convenio y Representante</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600">Convenio de Justicia Alternativa:</span>
              <Badge variant={formData.alternativeJustice ? "default" : "outline"} className="text-xs">
                {formData.alternativeJustice ? "Sí" : "No"}
              </Badge>
            </div>
            {formData.alternativeJustice && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">Tipo de persona del propietario:</span>
                  <Badge variant="outline" className="text-xs">
                    {formData.landlordType === "fisica" ? "Persona Física" : "Persona Moral"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">Representante del propietario:</span>
                  <Badge variant={formData.requiresOwnerRepresentative ? "default" : "outline"} className="text-xs">
                    {formData.requiresOwnerRepresentative ? "Sí" : "No"}
                  </Badge>
                </div>
              </>
            )}
            {formData.landlordEmail && (
              <div className="md:col-span-2 pt-2 border-t border-gray-100">
                <div className="flex items-start gap-2">
                  <span className="text-xs text-gray-600">Correo del Propietario:</span>
                  <span className="text-xs text-gray-900 break-all">{formData.landlordEmail}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Plantillas Seleccionadas */}
      <div className="space-y-4 border border-blue-200 rounded-lg p-5 bg-blue-50">
        <div>
          <h4 className="text-base font-semibold text-gray-900 mb-2">
            Plantillas que se van a agregar al expediente
          </h4>
          <p className="text-xs text-gray-600">
            Se creará un expediente con {selectedTemplates.length} plantilla
            {selectedTemplates.length !== 1 ? "s" : ""} seleccionadas automáticamente
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {selectedTemplates.map((slug) => (
            <Badge
              key={slug}
              className="bg-[#0f5ef7] hover:bg-[#0d52e0] text-white border-0 px-3 py-1"
            >
              {templateNames[slug] || slug}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
};
